<template>
  <CHeader fixed with-subheader light>
    <CToggler in-header class="ml-3 d-lg-none" @click="$store.commit('toggleSidebarMobile')" />
    <CToggler in-header class="ml-3 d-md-down-none" @click="$store.commit('toggleSidebarDesktop')" />
    <span class="header-greeting d-md-down-none ml-3">
      <span class="header-greeting__text">{{ greeting }}</span>
      <span v-if="userFirstName" class="header-greeting__name">, {{ userFirstName }}</span>
    </span>
    <span
      v-if="showDbIndicator"
      class="db-indicator ml-3"
      :class="`db-indicator--${dbEnvironment}`"
      :title="dbIndicatorTooltip"
    >
      <span class="db-indicator__dot" aria-hidden="true"></span>
      <span class="db-indicator__label">{{ dbIndicatorLabel }}</span>
    </span>
    <div ref="mobileOrgSwitcher" class="mobile-org-switcher mx-auto d-lg-none">
      <button
        type="button"
        class="mobile-org-switcher__trigger"
        :class="{
          'mobile-org-switcher__trigger--open': orgDropdownOpen,
          'mobile-org-switcher__trigger--static': !canSwitchOrganisation,
        }"
        :disabled="isUpdatingOrganisation || organisations.length === 0 || !canSwitchOrganisation"
        :aria-expanded="canSwitchOrganisation ? (orgDropdownOpen ? 'true' : 'false') : null"
        :aria-haspopup="canSwitchOrganisation ? 'listbox' : null"
        @click="toggleOrgDropdown"
      >
        <span class="mobile-org-switcher__avatar">
          <img
            v-if="mobileOrgBrandImage"
            :src="mobileOrgBrandImage"
            :alt="mobileOrgDisplayName"
          />
          <span v-else class="mobile-org-switcher__initials">
            {{ mobileOrgInitials }}
          </span>
        </span>
        <span class="mobile-org-switcher__info">
          <span class="mobile-org-switcher__label">Organisation</span>
          <span class="mobile-org-switcher__name">
            {{ mobileOrgDisplayName || 'Select organisation' }}
          </span>
        </span>
        <span v-if="isUpdatingOrganisation" class="mobile-org-switcher__spinner" aria-hidden="true"/>
        <svg
          v-else-if="canSwitchOrganisation"
          class="mobile-org-switcher__chevron"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      <transition name="mobile-org-switcher-fade">
        <div
          v-if="orgDropdownOpen && canSwitchOrganisation"
          class="mobile-org-switcher__menu"
          role="listbox"
        >
          <div class="mobile-org-switcher__menu-head">Switch organisation</div>
          <ul class="mobile-org-switcher__menu-list">
            <li
              v-for="organisation in organisations"
              :key="organisation.id"
              role="option"
              :aria-selected="organisation.id === activeOrganisationId ? 'true' : 'false'"
            >
              <button
                type="button"
                class="mobile-org-switcher__item"
                :class="{ 'mobile-org-switcher__item--active': organisation.id === activeOrganisationId }"
                @click="selectOrganisation(organisation.id)"
              >
                <span class="mobile-org-switcher__avatar mobile-org-switcher__avatar--sm">
                  <img
                    v-if="organisationImage(organisation)"
                    :src="organisationImage(organisation)"
                    :alt="organisation.name"
                  />
                  <span v-else class="mobile-org-switcher__initials">
                    {{ initialsFor(organisation.name) }}
                  </span>
                </span>
                <span class="mobile-org-switcher__item-name">{{ organisation.name }}</span>
                <svg
                  v-if="organisation.id === activeOrganisationId"
                  class="mobile-org-switcher__check"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </button>
            </li>
          </ul>
        </div>
      </transition>
    </div>

    <CMenu />

    <CHeaderNav class="ml-auto mr-2 mr-md-4 header-nav">
      <li class="c-header-nav-item spotlight-trigger-item">
        <button
          type="button"
          class="spotlight-trigger"
          aria-label="Search pages"
          @click="openSpotlight"
        >
          <svg class="spotlight-trigger__icon" viewBox="0 0 24 24" width="15" height="15" fill="none"
            stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7"/>
            <line x1="16.6" y1="16.6" x2="21" y2="21"/>
          </svg>
          <span class="spotlight-trigger__label">Search pages</span>
          <kbd class="spotlight-trigger__kbd">{{ spotlightShortcut }}</kbd>
        </button>
      </li>
      <li class="c-header-nav-item d-md-down-none header-clock" aria-label="Current time">
        <span class="header-clock__time">
          <span class="header-clock__hm">{{ clockTime }}</span>
          <span class="header-clock__ampm">{{ clockMeridiem }}</span>
        </span>
        <span class="header-clock__date">{{ clockDate }}</span>
      </li>
      <CDropdown v-if="canSeeInbox" inNav class="c-header-nav-items mx-1 mx-md-2 messages-dropdown" placement="bottom-end"
        add-menu-classes="pt-0 messages-dropdown__menu"
        @update:show="onInboxDropdownToggle">
        <template #toggler>
          <CHeaderNavLink class="messages-dropdown__toggle" :title="`${unreadMessageCount} unread`">
            <CIcon name="cil-bell" />
            <span v-if="unreadMessageCount > 0" class="messages-dropdown__badge">
              {{ unreadMessageCount > 9 ? '9+' : unreadMessageCount }}
            </span>
          </CHeaderNavLink>
        </template>

        <div class="messages-dropdown__header">
          <span class="messages-dropdown__title">Notifications</span>
          <div class="messages-dropdown__header-actions">
            <span v-if="unreadMessageCount > 0" class="messages-dropdown__count">
              {{ unreadMessageCount }} new
            </span>
            <button
              type="button"
              class="messages-dropdown__refresh"
              :class="{ 'is-loading': isRefreshingInbox }"
              :disabled="isRefreshingInbox"
              title="Refresh notifications"
              aria-label="Refresh notifications"
              @click.stop="refreshInbox"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2"
                stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
          </div>
        </div>

        <ul v-if="messages.length" class="messages-dropdown__list">
          <li v-for="message in messages" :key="message.id" class="messages-dropdown__item"
            :class="{ 'messages-dropdown__item--unread': message.unread }"
            @click="openInbox">
            <span class="messages-dropdown__avatar" :style="{ background: message.avatarColor }">
              {{ message.initials }}
            </span>
            <span class="messages-dropdown__body">
              <span class="messages-dropdown__row">
                <span class="messages-dropdown__sender">{{ message.sender }}</span>
                <span class="messages-dropdown__time">{{ message.time }}</span>
              </span>
              <span class="messages-dropdown__snippet">{{ message.snippet }}</span>
            </span>
            <span v-if="message.unread" class="messages-dropdown__dot" aria-hidden="true" />
          </li>
        </ul>
        <div v-else class="messages-dropdown__empty">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M22 12h-6l-2 3h-4l-2-3H2" />
            <path
              d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
          </svg>
          <span>You're all caught up.</span>
        </div>

        <div class="messages-dropdown__footer" @click="openInbox">
          <span class="messages-dropdown__view-all">View all in Inbox</span>
        </div>
      </CDropdown>
      <CDropdown inNav class="c-header-nav-items d-md-down-none mx-2 cal-dropdown" placement="bottom-end"
        add-menu-classes="pt-0 cal-dropdown__menu">
        <template #toggler>
          <CHeaderNavLink class="cal-dropdown__toggle" title="Calendar" aria-label="Calendar">
            <CIcon name="cil-calendar" />
            <span v-if="todayHolidayCount > 0" class="cal-dropdown__badge" :title="`${todayHolidayCount} event(s) today`">
              {{ todayHolidayCount }}
            </span>
          </CHeaderNavLink>
        </template>

        <div class="cal-dropdown__header">
          <button type="button" class="cal-dropdown__nav-btn" aria-label="Previous month" @click.stop="changeMiniMonth(-1)">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <div class="cal-dropdown__title-wrap">
            <span class="cal-dropdown__title">{{ miniMonthLabel }} {{ miniYear }}</span>
            <button type="button" class="cal-dropdown__today-link" @click.stop="resetMiniToToday">Today</button>
          </div>
          <button type="button" class="cal-dropdown__nav-btn" aria-label="Next month" @click.stop="changeMiniMonth(1)">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>

        <div class="cal-dropdown__weekdays">
          <span v-for="d in miniWeekdays" :key="d">{{ d }}</span>
        </div>

        <div class="cal-dropdown__grid">
          <button
            v-for="(cell, idx) in miniCells"
            :key="idx"
            type="button"
            class="cal-dropdown__cell"
            :class="{
              'cal-dropdown__cell--outside': !cell.inMonth,
              'cal-dropdown__cell--today': cell.isToday,
              'cal-dropdown__cell--has-holiday': cell.holidays.length > 0,
              'cal-dropdown__cell--weekend': cell.isWeekend,
            }"
            :title="cell.holidays.map((h) => h.name).join(', ') || ''"
            @click.stop="onMiniCellClick(cell)"
          >
            <span>{{ cell.day }}</span>
            <span v-if="cell.holidays.length > 0" class="cal-dropdown__cell-dot" aria-hidden="true"/>
          </button>
        </div>

        <div v-if="monthHolidaysShort.length > 0" class="cal-dropdown__events">
          <div class="cal-dropdown__events-head">This month</div>
          <ul class="cal-dropdown__events-list">
            <li v-for="(h, i) in monthHolidaysShort" :key="i" class="cal-dropdown__events-item">
              <span class="cal-dropdown__events-dot" :class="`cal-dropdown__events-dot--${h.type}`" aria-hidden="true"/>
              <span class="cal-dropdown__events-date">{{ formatMiniDate(h.date) }}</span>
              <span class="cal-dropdown__events-name">{{ h.name }}</span>
            </li>
          </ul>
        </div>

        <router-link to="/calendar" class="cal-dropdown__footer" @click.native="closeAnyOpenDropdown">
          View detailed view
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </router-link>
      </CDropdown>
      <CHeaderNavItem v-if="showCallQuickView" class="d-md-down-none mx-2">
        <CHeaderNavLink class="quick-replies-toggle" :class="{ 'quick-replies-toggle--active': callQuickViewOpen }"
          title="Quick calls" aria-label="Quick calls" @click.native.prevent="callQuickViewOpen = true">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path
              d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z" />
          </svg>
        </CHeaderNavLink>
      </CHeaderNavItem>
      <CHeaderNavItem class="d-md-down-none mx-2">
        <CHeaderNavLink class="quick-replies-toggle" :class="{ 'quick-replies-toggle--active': quickRepliesOpen }"
          title="Quick replies" aria-label="Quick replies" @click.native.prevent="quickRepliesOpen = true">
          <CIcon name="cil-speech" />
        </CHeaderNavLink>
      </CHeaderNavItem>
      <TheHeaderDropdownAccnt />
    </CHeaderNav>
    <TheHeaderQuickReplies :open="quickRepliesOpen" @close="quickRepliesOpen = false" />
    <TheHeaderCallQuickView v-if="showCallQuickView" :open="callQuickViewOpen" @close="callQuickViewOpen = false" />
    <CSubheader class="px-3 subheader-row">
      <router-link v-if="showCheckin" to="/attendance/my" class="sub-checkin" :class="{ 'sub-checkin--alert': isMapped && !checkedIn, 'sub-checkin--out': !isMapped }" :title="attendanceTooltip">
        <svg class="sub-checkin__icon" viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <polyline points="10 17 15 12 10 7" />
          <line x1="15" y1="12" x2="3" y2="12" />
        </svg>
        <span v-if="!isMapped" class="sub-checkin__text">Biometric not linked — tap to learn more</span>
        <span v-else-if="checkedIn" class="sub-checkin__text">
          Check In time: <strong class="sub-checkin__time">{{ checkInClock }}</strong>
        </span>
        <span v-else class="sub-checkin__text">Not checked in today</span>
      </router-link>
      <router-link
        :to="{ name: 'NetworkSpeed' }"
        class="net-status__link ml-auto"
        :class="`net-status__link--${networkState}`"
        :title="networkTooltip"
        aria-label="Network status"
      >
        <span class="net-status__dot" aria-hidden="true"></span>
        <span class="net-status__label">{{ networkLabel }}</span>
      </router-link>
    </CSubheader>
  </CHeader>
</template>

<script>
import axios from 'axios'
import TheHeaderDropdownAccnt from './TheHeaderDropdownAccnt'
import TheHeaderQuickReplies from './TheHeaderQuickReplies'
import TheHeaderCallQuickView from './TheHeaderCallQuickView'
import {
  getHolidaysForDate,
  getHolidaysForMonth,
} from '@/views/calendar/holidays'
import { shouldShowDbIndicator } from '@/utils/envBadge'

const DEFAULT_LOGO = '/mcci_logo.png'
const CALL_QV_ROLES = ['telesales', 'admin', 'superadmin']
const INBOX_ROLES = ['superadmin', 'admin', 'accountant', 'finance_manager', 'senior_accountant_2']
// Refresh cadence for the bell badge / dropdown. Inbox items are
// minutes-scale events (approvals, reminders) — not real-time signals — so
// a 5-minute poll keeps the badge fresh without hammering the API. The
// dropdown's manual refresh button gives users an on-demand way to pull
// fresher state when they actually open the bell.
const INBOX_POLL_MS = 300000
const INBOX_DROPDOWN_LIMIT = 6
// Header attendance chip. Punches happen only a handful of times a day
// (morning in, evening out), so a 15-minute poll keeps the chip honest
// without adding to the API churn the Hostinger conn cap is sensitive to.
// A tab-focus refresh covers the "just walked in and opened the laptop" case.
const ATTENDANCE_POLL_MS = 900000
const INBOX_TYPE_LABELS = {
  discount_approval: 'Discount approval',
  payment_overdue_reminder: 'Payment overdue',
  counter_mismatch_reminder: 'Counter mismatch',
}
// Stable colour per inbox type so the dropdown reads at a glance —
// approvals always look the same green, reminders always grey, etc.
const INBOX_TYPE_COLORS = {
  discount_approval:       'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
  payment_overdue_reminder:'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
  counter_mismatch_reminder:'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 100%)',
}
const INBOX_DEFAULT_COLOR = 'linear-gradient(135deg, #4b5563 0%, #9ca3af 100%)'
// Header network-status pill. We re-use Cloudflare's 1KB speed-test endpoint —
// same origin NetworkSpeed.vue uses — so we don't have to spin up a server
// /ping route and the measurement reflects real internet latency rather than
// app-server health. 25s feels live without being chatty; navigator.onLine
// catches disconnects instantly so the cadence only sets the freshness of
// "weak / unstable" classifications.
const NETWORK_PING_URL = 'https://speed.cloudflare.com/__down?bytes=1000'
const NETWORK_POLL_MS = 25000
const NETWORK_PING_TIMEOUT_MS = 5000
const NETWORK_PING_SAMPLE_SIZE = 3
// Thresholds mirror NetworkSpeed.vue's qualitative scale so the header pill
// agrees with the full diagnostic page.
const PING_STABLE_MAX_MS = 150
const PING_WEAK_MAX_MS = 300
const MINI_MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
]
const MINI_WEEKDAY_LABELS = ['S','M','T','W','T','F','S']

const NOTIF_SOUND_SRC = '/soundfx/notif.mp3'
let notifAudioEl = null
function playNotifSound() {
  try {
    if (!notifAudioEl) {
      notifAudioEl = new Audio(NOTIF_SOUND_SRC)
      notifAudioEl.preload = 'auto'
    }
    notifAudioEl.currentTime = 0
    const p = notifAudioEl.play()
    if (p && typeof p.catch === 'function') p.catch(() => {})
  } catch (e) {
    // autoplay policy or unsupported — silent fail is fine
  }
}

function toIso(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'TheHeader',
  components: {
    TheHeaderDropdownAccnt,
    TheHeaderQuickReplies,
    TheHeaderCallQuickView,
  },
  data() {
    const now = new Date()
    return {
      now,
      clockTimer: null,
      quickRepliesOpen: false,
      callQuickViewOpen: false,
      miniYear: now.getFullYear(),
      miniMonth: now.getMonth(),
      miniWeekdays: MINI_WEEKDAY_LABELS,
      inboxItems: [],
      inboxUnreadCount: 0,
      inboxCountInitialized: false,
      inboxPollTimer: null,
      isRefreshingInbox: false,
      networkState: 'checking',
      lastPingMs: null,
      recentPings: [],
      networkTimer: null,
      networkAbortCtrl: null,
      orgDropdownOpen: false,
      isUpdatingOrganisation: false,
      attendanceMapped: null,
      attendanceTodayRow: null,
      attendancePollTimer: null,
      dbEnvironment: null,
    }
  },
  computed: {
    // Mac users expect ⌘K, everyone else Ctrl K. Read once from the platform
    // string; the palette itself accepts either modifier regardless.
    spotlightShortcut() {
      const platform = (window.navigator && (window.navigator.platform || '')) || ''
      return /Mac|iPhone|iPad/i.test(platform) ? '⌘K' : 'Ctrl K'
    },
    activeOrganisationName() {
      return this.$store.getters.activeOrganisationDisplayName || ''
    },
    organisations() {
      return this.$store.state.organisations
    },
    activeOrganisationId() {
      return this.$store.state.activeOrganisationId
    },
    activeOrganisation() {
      return this.$store.getters.activeOrganisation
    },
    // Mirrors TheSidebar.vue's all-orgs-route detection so the mobile chip
    // shows "All Organisations" on multi-org pages instead of a stale name.
    isAllOrganisationsRoute() {
      if (
        this.$route.path === '/orders'
        && (this.$route.query.tab === 'all' || this.$route.query.tab === 'search')
      ) {
        return true
      }
      return this.$route.path === '/analytics'
        || this.$route.path === '/shipping/dashboard'
        || this.$route.path === '/websites'
        || this.$route.path === '/pos'
        || this.$route.path === '/hms_imports'
        || this.$route.path === '/tele_sales_accounts'
        || this.$route.path === '/incentive_preview'
        || this.$route.path === '/inbox'
    },
    canSwitchOrganisation() {
      if (this.isAllOrganisationsRoute) {
        return false
      }
      return this.$store.getters.can('switch_org') && this.organisations.length > 1
    },
    mobileOrgDisplayName() {
      if (this.isAllOrganisationsRoute) {
        return 'All Organisations'
      }
      return this.activeOrganisationName
    },
    mobileOrgBrandImage() {
      if (this.isAllOrganisationsRoute) {
        return null
      }
      return this.imageForName(this.activeOrganisationName)
    },
    mobileOrgInitials() {
      if (this.isAllOrganisationsRoute) {
        return 'ALL'
      }
      return this.initialsFor(this.activeOrganisationName)
    },
    // Bell badge — counts open items only. We trust the server's
    // unread-count endpoint as the source of truth (it scans the full
    // table), so the badge stays accurate even when the dropdown is
    // limited to INBOX_DROPDOWN_LIMIT rows.
    unreadMessageCount() {
      return this.inboxUnreadCount
    },
    // Dropdown payload — maps real inbox rows into the existing
    // sender/snippet/time/avatar shape the template already renders.
    // Cap at INBOX_DROPDOWN_LIMIT so a flood of items doesn't blow up
    // the menu height; "View all in Inbox" leads to the full list.
    messages() {
      return this.inboxItems.slice(0, INBOX_DROPDOWN_LIMIT).map((item) => ({
        id: item.id,
        sender: INBOX_TYPE_LABELS[item.type] || this.formatInboxLabel(item.type),
        initials: this.inboxInitials(item.type),
        avatarColor: INBOX_TYPE_COLORS[item.type] || INBOX_DEFAULT_COLOR,
        snippet: item.title || item.summary || '—',
        time: this.formatInboxRelative(item.created_at),
        unread: item.status === 'open',
      }))
    },
    showCallQuickView() {
      const raw = localStorage.getItem('roles') || ''
      const roles = raw.split(',').map((r) => r.trim().toLowerCase()).filter(Boolean)
      return roles.some((role) => CALL_QV_ROLES.includes(role))
    },
    canSeeInbox() {
      const raw = localStorage.getItem('roles') || ''
      const roles = raw.split(',').map((r) => r.trim().toLowerCase()).filter(Boolean)
      return roles.some((role) => INBOX_ROLES.includes(role))
    },
    authUser() {
      try {
        const raw = localStorage.getItem('auth_user')
        return raw ? JSON.parse(raw) : null
      } catch (error) {
        return null
      }
    },
    userFirstName() {
      const user = this.authUser
      if (!user) {
        return ''
      }

      const name = typeof user.name === 'string' ? user.name.trim() : ''
      if (name) {
        return name.split(/\s+/)[0]
      }

      const email = typeof user.email === 'string' ? user.email.trim() : ''
      if (email) {
        const local = email.split('@')[0]
        return local ? local[0].toUpperCase() + local.slice(1) : ''
      }

      return ''
    },
    greeting() {
      const hour = this.now.getHours()
      if (hour < 12) {
        return 'Good morning'
      }
      if (hour < 17) {
        return 'Good afternoon'
      }
      return 'Good evening'
    },
    organisationLogo() {
      const lower = this.activeOrganisationName.toLowerCase()

      if (lower.includes('agro')) {
        return '/MCCI-Agro.png'
      }

      if (lower.includes('drug')) {
        return '/MCCI-Drugs.png'
      }

      return DEFAULT_LOGO
    },
    organisationLogoAlt() {
      return this.activeOrganisationName || 'Logo'
    },
    clockTime() {
      let hours = this.now.getHours() % 12
      if (hours === 0) {
        hours = 12
      }
      const minutes = String(this.now.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    clockMeridiem() {
      return this.now.getHours() >= 12 ? 'PM' : 'AM'
    },
    clockDate() {
      return this.now.toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      })
    },
    todayIso() {
      return toIso(this.now)
    },
    todayHolidayCount() {
      return getHolidaysForDate(this.todayIso).length
    },
    miniMonthLabel() {
      return MINI_MONTH_NAMES[this.miniMonth]
    },
    miniCells() {
      const firstOfMonth = new Date(this.miniYear, this.miniMonth, 1)
      const startWeekday = firstOfMonth.getDay()
      const start = new Date(this.miniYear, this.miniMonth, 1 - startWeekday)

      const cells = []
      for (let i = 0; i < 42; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        const iso = toIso(d)
        cells.push({
          date: d,
          iso,
          day: d.getDate(),
          inMonth: d.getMonth() === this.miniMonth,
          isToday: iso === this.todayIso,
          isWeekend: d.getDay() === 0 || d.getDay() === 6,
          holidays: getHolidaysForDate(iso),
        })
      }
      return cells
    },
    monthHolidaysShort() {
      return getHolidaysForMonth(this.miniYear, this.miniMonth).slice(0, 4)
    },
    networkLabel() {
      switch (this.networkState) {
        case 'stable':   return 'Stable'
        case 'weak':     return 'Weak'
        case 'unstable': return 'Unstable'
        case 'offline':  return 'Offline'
        default:         return 'Checking…'
      }
    },
    networkTooltip() {
      if (this.networkState === 'offline') {
        return 'No internet connection — click for full speed test'
      }
      if (this.networkState === 'checking') {
        return 'Measuring network…'
      }
      const ms = this.lastPingMs == null ? '—' : `${Math.round(this.lastPingMs)} ms`
      return `Network: ${this.networkLabel} (${ms}) — click for full speed test`
    },
    // ── Attendance check-in (shown in the subheader) ─────────────────
    // Renders once the my-hours call has resolved (mapped true OR false).
    // Mapped + punched → check-in time; mapped + no punch → "Not checked in
    // today"; not mapped → a "biometric not linked" hint. Stays hidden while
    // the state is still unknown (null) or after a load error.
    showCheckin() {
      return this.attendanceMapped !== null
    },
    isMapped() {
      return this.attendanceMapped === true
    },
    checkedIn() {
      return !!this.attendanceTodayRow
    },
    checkInClock() {
      const row = this.attendanceTodayRow
      return row ? this.formatPunchTime(row.first_in) : ''
    },
    attendanceTooltip() {
      if (!this.isMapped) return 'Your biometric PIN isn’t linked yet — ask an administrator to map you'
      const row = this.attendanceTodayRow
      if (!row) return "You haven't checked in today"
      const inTime = this.formatPunchTime(row.first_in)
      if (row.punches > 1) {
        return `Checked in ${inTime} · Out ${this.formatPunchTime(row.last_out)} · ${this.formatHoursShort(row.hours)} on-site`
      }
      return `Checked in at ${inTime}`
    },
    // ── DB-connection indicator ─────────────────────────────────────
    // Only render once the /api/app-meta call has resolved to a known
    // label. The frontend-env gate (shouldShowDbIndicator) already ran
    // before the fetch, so reaching a non-null dbEnvironment means we're
    // on local/UAT and the server told us which DB it's wired to.
    showDbIndicator() {
      return this.dbEnvironment === 'qa' || this.dbEnvironment === 'prod'
    },
    dbIndicatorLabel() {
      return this.dbEnvironment === 'prod' ? 'PROD DB' : 'QA DB'
    },
    dbIndicatorTooltip() {
      return this.dbEnvironment === 'prod'
        ? 'This frontend is connected to the PRODUCTION database — changes affect real data'
        : 'This frontend is connected to the QA / UAT database — safe for testing'
    },
  },
  methods: {
    openSpotlight() {
      this.$root.$emit('spotlight:open')
    },

    // ── Mobile organisation switcher ────────────────────────────────
    // Mirrors TheSidebar.vue's switcher so mobile users (who never see the
    // sidebar's chip) can still change the active organisation. The store
    // mutation flips immediately; the API call patches the server's record
    // of "my last active org" and we revert on failure. Keeping the logic
    // here rather than reaching into the sidebar avoids cross-component
    // coupling for what is a self-contained mobile control.
    imageForName(name) {
      const lower = (name || '').toLowerCase()
      if (lower.includes('agro')) {
        return '/MCCI-Agro.png'
      }
      if (lower.includes('drug')) {
        return '/MCCI-Drugs.png'
      }
      return null
    },
    organisationImage(organisation) {
      return this.imageForName(organisation && organisation.name)
    },
    initialsFor(name) {
      if (!name) {
        return '?'
      }
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
    },
    toggleOrgDropdown() {
      if (
        this.isUpdatingOrganisation
        || this.organisations.length === 0
        || !this.canSwitchOrganisation
      ) {
        return
      }
      this.orgDropdownOpen = !this.orgDropdownOpen
    },
    closeOrgDropdown() {
      this.orgDropdownOpen = false
    },
    selectOrganisation(id) {
      this.orgDropdownOpen = false
      if (Number(id) === Number(this.activeOrganisationId) || this.isUpdatingOrganisation) {
        return
      }
      this.handleOrganisationChange(id)
    },
    handleOrgDocumentClick(event) {
      const switcher = this.$refs.mobileOrgSwitcher
      if (!switcher || switcher.contains(event.target)) {
        return
      }
      this.closeOrgDropdown()
    },
    handleOrgEscape(event) {
      if (event.key === 'Escape') {
        this.closeOrgDropdown()
      }
    },
    handleOrganisationChange(nextOrgId) {
      const previousOrgId = this.activeOrganisationId
      const previousOrg = this.activeOrganisation
      const id = Number(nextOrgId)

      if (!id || id === previousOrgId) {
        return
      }

      this.isUpdatingOrganisation = true
      const nextOrg = this.organisations.find((organisation) => Number(organisation.id) === id) || null
      this.$store.commit('setActiveOrganisationContext', {
        id,
        name: nextOrg ? nextOrg.name : '',
      })

      const self = this
      axios.post(this.$apiAdress + '/api/organisation/change_my_org', {
        organisation_id: id,
      }).catch(function (error) {
        console.log(error)
        self.$store.commit('setActiveOrganisationContext', {
          id: previousOrgId,
          name: previousOrg ? previousOrg.name : '',
        })
      }).finally(function () {
        self.isUpdatingOrganisation = false
      })
    },
    changeMiniMonth(delta) {
      let m = this.miniMonth + delta
      let y = this.miniYear
      if (m < 0) { m = 11; y -= 1 }
      if (m > 11) { m = 0; y += 1 }
      this.miniMonth = m
      this.miniYear = y
    },
    resetMiniToToday() {
      this.miniYear = this.now.getFullYear()
      this.miniMonth = this.now.getMonth()
    },
    onMiniCellClick(cell) {
      if (!cell.inMonth) {
        this.miniYear = cell.date.getFullYear()
        this.miniMonth = cell.date.getMonth()
      }
    },
    formatMiniDate(iso) {
      const [, m, d] = iso.split('-').map(Number)
      return `${String(d).padStart(2, '0')} ${MINI_MONTH_NAMES[m - 1].slice(0, 3)}`
    },
    closeAnyOpenDropdown() {
      // Click the body so the open CDropdown menu collapses; the router-link
      // navigation otherwise leaves it visually open behind the new page.
      document.body.click()
    },
    // Inbox bell — two endpoints with very different cost profiles:
    //   • /api/inbox/unread-count — cheap, single COUNT query. Runs on the
    //     5-min poll, on tab focus, and as part of any manual refresh.
    //   • /api/inbox?status=open  — heavy, returns up to 500 rows with role
    //     filtering. Only fetched on demand: when the dropdown actually
    //     opens, when the user clicks the refresh button, or when they
    //     manually navigate to /inbox.
    // Both fail silently — the bell shouldn't flash an error banner over
    // the chrome.
    loadInboxCount(manual = false) {
      // Skip the scheduled poll when the tab is hidden — background tabs
      // were the biggest single drain on the Hostinger 500 conn/hr cap.
      // Manual triggers (bell click, dropdown open, visibility regain) all
      // pass manual=true and bypass this gate.
      if (!manual && typeof document !== 'undefined' && document.hidden) {
        return Promise.resolve()
      }
      return axios
        .get(`${this.$apiAdress}/api/inbox/unread-count`)
        .then((response) => {
          const next = Number(response.data && response.data.count) || 0
          // Chime only on a strict increase after the first load, so the bell
          // doesn't ring on mount or when items get cleared.
          if (this.inboxCountInitialized && next > this.inboxUnreadCount) {
            playNotifSound()
          }
          this.inboxUnreadCount = next
          this.inboxCountInitialized = true
        })
        .catch(() => {
          this.inboxUnreadCount = 0
        })
    },
    loadInboxList() {
      return axios
        .get(`${this.$apiAdress}/api/inbox`, { params: { status: 'open' } })
        .then((response) => {
          this.inboxItems = Array.isArray(response.data) ? response.data : []
        })
        .catch(() => {
          this.inboxItems = []
        })
    },
    refreshInbox() {
      // Bell refresh button — pull both the badge and the list together.
      this.isRefreshingInbox = true
      Promise.all([this.loadInboxCount(true), this.loadInboxList()]).finally(() => {
        this.isRefreshingInbox = false
      })
    },
    onInboxDropdownToggle(open) {
      // CoreUI emits update:show on both open and close. Only fetch when
      // transitioning to open so closing the bell doesn't trigger a call.
      if (open) {
        this.loadInboxList()
        // Refresh the badge alongside so the count and the list rendered
        // in the dropdown can't drift out of sync.
        this.loadInboxCount(true)
      }
    },
    openInbox() {
      this.closeAnyOpenDropdown()
      if (this.$route && this.$route.path === '/inbox') return
      this.$router.push('/inbox').catch(() => {})
    },
    inboxInitials(type) {
      if (!type) return 'IN'
      const parts = String(type).split('_').filter(Boolean)
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return parts[0].slice(0, 2).toUpperCase()
    },
    formatInboxLabel(value) {
      if (!value) return 'Notification'
      const str = String(value).replace(/_/g, ' ')
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    formatInboxRelative(value) {
      if (!value) return ''
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return ''
      const diffMs = Date.now() - d.getTime()
      const diffMin = Math.floor(diffMs / 60000)
      if (diffMin < 1) return 'now'
      if (diffMin < 60) return `${diffMin}m`
      const diffHr = Math.floor(diffMin / 60)
      if (diffHr < 24) return `${diffHr}h`
      const diffDays = Math.floor(diffHr / 24)
      if (diffDays < 7) return `${diffDays}d`
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
    },
    // ── Attendance chip ─────────────────────────────────────────────
    formatPunchTime(t) {
      if (!t) return '—'
      const [hStr, mStr] = String(t).split(':')
      let h = Number(hStr)
      const m = mStr || '00'
      if (!Number.isFinite(h)) return t
      const suffix = h >= 12 ? 'PM' : 'AM'
      h = h % 12 || 12
      return `${h}:${m} ${suffix}`
    },
    formatHoursShort(hours) {
      const val = Number(hours) || 0
      const h = Math.floor(val)
      const m = Math.round((val - h) * 60)
      if (m === 0) return `${h}h`
      return `${h}h ${String(m).padStart(2, '0')}m`
    },
    loadMyAttendance(manual = false) {
      // Skip the scheduled poll on a hidden tab, same as the inbox poll —
      // background tabs were the biggest drain on the conn cap.
      if (!manual && typeof document !== 'undefined' && document.hidden) {
        return Promise.resolve()
      }
      const today = toIso(new Date())
      return axios
        .get(`${this.$apiAdress}/api/attendance/my-hours`, { params: { from: today, to: today } })
        .then((response) => {
          const data = response.data || {}
          this.attendanceMapped = data.mapped !== false
          const rows = Array.isArray(data.rows) ? data.rows : []
          this.attendanceTodayRow = rows.find((r) => r.date === today) || rows[0] || null
        })
        .catch(() => {
          // Never surface attendance errors in the chrome. Reset to "unknown"
          // (not false) so a failed request can't masquerade as "not linked".
          this.attendanceMapped = null
          this.attendanceTodayRow = null
        })
    },
    loadDbEnvironment() {
      // Ask the server which DB it's actually connected to. Only called on
      // non-prod frontends (see mounted). Fails silently — the indicator is a
      // convenience, never a blocker for the chrome.
      return axios
        .get(`${this.$apiAdress}/api/app-meta`)
        .then((response) => {
          const env = response.data && response.data.db_environment
          this.dbEnvironment = env === 'prod' || env === 'qa' ? env : null
        })
        .catch(() => {
          this.dbEnvironment = null
        })
    },
    async pingNetwork() {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        this.networkState = 'offline'
        this.lastPingMs = null
        this.recentPings = []
        return
      }

      if (this.networkAbortCtrl) {
        this.networkAbortCtrl.abort()
      }
      const ctrl = new AbortController()
      this.networkAbortCtrl = ctrl
      const timeoutId = setTimeout(() => ctrl.abort(), NETWORK_PING_TIMEOUT_MS)

      const t0 = performance.now()
      try {
        const res = await fetch(NETWORK_PING_URL, {
          method: 'GET',
          cache: 'no-store',
          signal: ctrl.signal,
        })
        // Drain the body so the timing reflects an honest end-to-end fetch,
        // not just the headers landing.
        await res.arrayBuffer()
        const elapsed = performance.now() - t0
        this.lastPingMs = elapsed
        this.recordPingSample(elapsed)
        this.networkState = this.classifyPing(this.smoothedPing())
      } catch (err) {
        // A failed fetch with navigator.onLine still true usually means
        // hiccup, not full disconnect — surface as 'unstable' so we don't
        // pretend the browser knows more than it does.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          this.networkState = 'offline'
        } else {
          this.networkState = 'unstable'
        }
        this.lastPingMs = null
      } finally {
        clearTimeout(timeoutId)
        if (this.networkAbortCtrl === ctrl) {
          this.networkAbortCtrl = null
        }
      }
    },
    recordPingSample(ms) {
      this.recentPings.push(ms)
      if (this.recentPings.length > NETWORK_PING_SAMPLE_SIZE) {
        this.recentPings.shift()
      }
    },
    smoothedPing() {
      if (this.recentPings.length === 0) return this.lastPingMs
      const sorted = [...this.recentPings].sort((a, b) => a - b)
      return sorted[Math.floor(sorted.length / 2)]
    },
    classifyPing(ms) {
      if (ms == null) return 'checking'
      if (ms <= PING_STABLE_MAX_MS) return 'stable'
      if (ms <= PING_WEAK_MAX_MS) return 'weak'
      return 'unstable'
    },
    handleNetworkOnline() {
      // Browser says we're back — re-measure rather than optimistically
      // jumping to 'stable'; the link may be flapping.
      this.networkState = 'checking'
      this.recentPings = []
      this.pingNetwork()
    },
    handleNetworkOffline() {
      this.networkState = 'offline'
      this.lastPingMs = null
      this.recentPings = []
      if (this.networkAbortCtrl) {
        this.networkAbortCtrl.abort()
      }
    },
    handleVisibilityChange() {
      // When the tab comes back to the foreground, refresh the badge
      // immediately instead of waiting up to 5 min for the next poll tick.
      // List is not fetched here — it only loads when the user actually
      // opens the dropdown.
      if (typeof document !== 'undefined' && !document.hidden) {
        if (this.canSeeInbox) {
          this.loadInboxCount(true)
        }
        // Refresh the attendance chip too — covers the staffer who punches in
        // and immediately opens/returns to the dashboard.
        this.loadMyAttendance(true)
      }
    },
  },
  mounted() {
    const tickToNextMinute = () => {
      this.now = new Date()
      const msToNextMinute = 60000 - (this.now.getSeconds() * 1000 + this.now.getMilliseconds())
      this.clockTimer = setTimeout(() => {
        this.now = new Date()
        this.clockTimer = setInterval(() => {
          this.now = new Date()
        }, 60000)
      }, msToNextMinute)
    }
    tickToNextMinute()

    if (this.canSeeInbox) {
      this.loadInboxCount(true)
      this.inboxPollTimer = setInterval(this.loadInboxCount, INBOX_POLL_MS)
    }

    this.loadMyAttendance(true)
    this.attendancePollTimer = setInterval(this.loadMyAttendance, ATTENDANCE_POLL_MS)

    // Only the real prod frontend is guaranteed to hit the prod DB, so we skip
    // the indicator (and its request) there; local/UAT ask the server which DB
    // they landed on.
    if (shouldShowDbIndicator()) {
      this.loadDbEnvironment()
    }

    this.pingNetwork()
    this.networkTimer = setInterval(this.pingNetwork, NETWORK_POLL_MS)
    window.addEventListener('online', this.handleNetworkOnline)
    window.addEventListener('offline', this.handleNetworkOffline)

    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    document.addEventListener('mousedown', this.handleOrgDocumentClick)
    document.addEventListener('keydown', this.handleOrgEscape)
  },
  beforeDestroy() {
    if (this.clockTimer) {
      clearTimeout(this.clockTimer)
      clearInterval(this.clockTimer)
      this.clockTimer = null
    }
    if (this.inboxPollTimer) {
      clearInterval(this.inboxPollTimer)
      this.inboxPollTimer = null
    }
    if (this.attendancePollTimer) {
      clearInterval(this.attendancePollTimer)
      this.attendancePollTimer = null
    }
    if (this.networkTimer) {
      clearInterval(this.networkTimer)
      this.networkTimer = null
    }
    if (this.networkAbortCtrl) {
      this.networkAbortCtrl.abort()
      this.networkAbortCtrl = null
    }
    window.removeEventListener('online', this.handleNetworkOnline)
    window.removeEventListener('offline', this.handleNetworkOffline)
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    document.removeEventListener('mousedown', this.handleOrgDocumentClick)
    document.removeEventListener('keydown', this.handleOrgEscape)
  },
}
</script>

<style scoped>
/* ── Spotlight trigger ───────────────────────────────────────────── */
.spotlight-trigger-item {
  display: flex;
  align-items: center;
  padding-right: 0.6rem;
  margin-right: 0.15rem;
  border-right: 1px solid #e5e9f2;
}

.spotlight-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 32px;
  padding: 0 0.5rem 0 0.65rem;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  background: #ffffff;
  color: #6b7280;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.spotlight-trigger:hover {
  border-color: #cbd5e1;
  color: #334155;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.06);
}

.spotlight-trigger:focus {
  outline: none;
}

.spotlight-trigger::-moz-focus-inner {
  border: 0;
}

.spotlight-trigger:focus-visible {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.spotlight-trigger__icon {
  flex: 0 0 auto;
  color: #94a3b8;
}

.spotlight-trigger:hover .spotlight-trigger__icon {
  color: #16a07a;
}

.spotlight-trigger__label {
  font-size: 0.78rem;
  font-weight: 500;
  white-space: nowrap;
}

.spotlight-trigger__kbd {
  padding: 0.08rem 0.34rem;
  border: 1px solid #e5e9f2;
  border-radius: 5px;
  background: #f8fafc;
  font-family: inherit;
  font-size: 0.66rem;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

/* Below md there's no keyboard to press ⌘K on and no room for the pill, so
   the trigger collapses to the icon — still the only way in on a phone. */
@media (max-width: 767.98px) {
  .spotlight-trigger-item {
    padding-right: 0;
    margin-right: 0;
    border-right: 0;
  }

  .spotlight-trigger {
    width: 34px;
    padding: 0;
    justify-content: center;
    border-color: transparent;
    background: transparent;
  }

  .spotlight-trigger__label,
  .spotlight-trigger__kbd {
    display: none;
  }

  .spotlight-trigger__icon {
    width: 17px;
    height: 17px;
  }
}

.header-clock {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  padding: 0 0.9rem;
  margin-right: 0.25rem;
  border-right: 1px solid #e5e9f2;
  line-height: 1;
  user-select: none;
}

.header-clock__time {
  display: flex;
  align-items: baseline;
  gap: 0.22rem;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}

.header-clock__hm {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.header-clock__ampm {
  font-size: 0.62rem;
  font-weight: 700;
  color: #16a07a;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.header-clock__date {
  margin-top: 3px;
  font-size: 0.66rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ── Subheader row ───────────────────────────────────────────────── */
.subheader-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.5rem;
}

/* CBreadcrumb wraps its <ol> in a <nav>; keep that block from stretching
   past its content so the network pill can sit on the right end of the
   subheader row. */
.subheader-row ::v-deep .breadcrumb {
  flex: 0 1 auto;
  margin-right: auto;
}

/* ── Network status pill ─────────────────────────────────────────── */
.net-status__link {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem 0.2rem 0.5rem;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  background: #ffffff;
  text-decoration: none;
  line-height: 1;
  flex: 0 0 auto;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease, box-shadow 0.12s ease;
}

.net-status__link:hover {
  text-decoration: none;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.net-status__link:focus { outline: 0; }
.net-status__link::-moz-focus-inner { border: 0; }
.net-status__link:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.net-status__dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #94a3b8;
}

.net-status__label {
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.net-status__link--checking {
  color: #6b7280;
}
.net-status__link--checking .net-status__dot {
  background: #94a3b8;
  animation: net-status-pulse 1.2s ease-in-out infinite;
}

.net-status__link--stable {
  color: #0e5b46;
  background: rgba(22, 160, 122, 0.08);
  border-color: rgba(22, 160, 122, 0.35);
}
.net-status__link--stable .net-status__dot {
  background: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.2);
}

.net-status__link--weak {
  color: #b45309;
  background: rgba(245, 158, 11, 0.08);
  border-color: rgba(245, 158, 11, 0.35);
}
.net-status__link--weak .net-status__dot {
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
}

.net-status__link--unstable {
  color: #b91c1c;
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.35);
}
.net-status__link--unstable .net-status__dot {
  background: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
  animation: net-status-pulse 1s ease-in-out infinite;
}

.net-status__link--offline {
  color: #475569;
  background: #f1f5f9;
  border-color: #cbd5e1;
}
.net-status__link--offline .net-status__dot {
  background: #6b7280;
}

@keyframes net-status-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.55; transform: scale(0.9); }
}

.messages-dropdown__toggle {
  position: relative;
}

.messages-dropdown__badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  letter-spacing: 0.02em;
  box-shadow: 0 0 0 2px #ffffff;
}

.messages-dropdown ::v-deep .messages-dropdown__menu {
  width: 340px;
  padding: 0;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.messages-dropdown__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  background: #fbfcfe;
  border-bottom: 1px solid #e5e9f2;
}

.messages-dropdown__header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.messages-dropdown__title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: 0.01em;
}

.messages-dropdown__count {
  font-size: 0.66rem;
  font-weight: 700;
  color: #16a07a;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.messages-dropdown__header-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
}

.messages-dropdown__refresh {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 6px;
  color: #4b5563;
  cursor: pointer;
  padding: 0;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.messages-dropdown__refresh:hover:not(:disabled) {
  background: #f5fbf8;
  border-color: #16a07a;
  color: #0e5b46;
}

.messages-dropdown__refresh:hover:not(:disabled) svg {
  transform: rotate(60deg);
}

.messages-dropdown__refresh:focus,
.messages-dropdown__refresh:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.messages-dropdown__refresh:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.messages-dropdown__refresh svg {
  transition: transform 0.25s ease;
}

.messages-dropdown__refresh.is-loading svg {
  animation: messages-dropdown-spin 0.8s linear infinite;
}

@keyframes messages-dropdown-spin {
  to { transform: rotate(360deg); }
}

.messages-dropdown__list {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 360px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 23, 42, 0.18) transparent;
}

.messages-dropdown__list::-webkit-scrollbar {
  width: 6px;
}

.messages-dropdown__list::-webkit-scrollbar-track {
  background: transparent;
}

.messages-dropdown__list::-webkit-scrollbar-thumb {
  background-color: rgba(15, 23, 42, 0.16);
  border-radius: 999px;
}

.messages-dropdown__list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(15, 23, 42, 0.28);
}

.messages-dropdown__item {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #f1f3f8;
  cursor: pointer;
  transition: background 0.12s ease;
}

.messages-dropdown__item:last-child {
  border-bottom: 0;
}

.messages-dropdown__item:hover {
  background: #f7f9fc;
}

.messages-dropdown__item--unread {
  background: rgba(22, 160, 122, 0.04);
}

.messages-dropdown__item--unread:hover {
  background: rgba(22, 160, 122, 0.08);
}

.messages-dropdown__avatar {
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.18);
}

.messages-dropdown__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.messages-dropdown__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}

.messages-dropdown__sender {
  font-size: 0.8rem;
  font-weight: 600;
  color: #1f2a44;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.messages-dropdown__time {
  flex: 0 0 auto;
  font-size: 0.68rem;
  font-weight: 500;
  color: #6b7280;
}

.messages-dropdown__snippet {
  font-size: 0.74rem;
  color: #4b5563;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.messages-dropdown__item--unread .messages-dropdown__sender {
  color: #0f172a;
}

.messages-dropdown__item--unread .messages-dropdown__snippet {
  color: #1f2a44;
}

.messages-dropdown__dot {
  position: absolute;
  top: 50%;
  right: 0.85rem;
  transform: translateY(-50%);
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16a07a;
  box-shadow: 0 0 0 2px rgba(22, 160, 122, 0.18);
}

.messages-dropdown__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  padding: 1.6rem 1rem;
  color: #94a3b8;
  font-size: 0.78rem;
  font-weight: 500;
}

.messages-dropdown__empty svg {
  color: #cbd5e1;
}

.messages-dropdown__footer {
  padding: 0.6rem 1rem;
  text-align: center;
  background: #fbfcfe;
  border-top: 1px solid #e5e9f2;
  cursor: pointer;
  transition: background 0.12s ease;
}

.messages-dropdown__footer:hover {
  background: #f1f4fa;
}

.messages-dropdown__view-all {
  font-size: 0.74rem;
  font-weight: 600;
  color: #16a07a;
  letter-spacing: 0.02em;
}

.header-greeting {
  display: inline-flex;
  align-self: center;
  align-items: baseline;
  gap: 0.18rem;
  font-size: 0.92rem;
  font-weight: 500;
  color: #4b5563;
  letter-spacing: 0.005em;
  white-space: nowrap;
}

.header-greeting__text {
  color: #4b5563;
}

.header-greeting__name {
  font-weight: 700;
  color: #1f2a44;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* ── DB-connection indicator ─────────────────────────────────────── */
/* Shown only on local / UAT frontends. QA reads as a calm amber pill;
   PROD is a loud red warning because touching real data from a non-prod
   frontend is the mistake we're guarding against. */
.db-indicator {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.22rem 0.6rem;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 0.64rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  line-height: 1;
  white-space: nowrap;
  user-select: none;
}

.db-indicator__dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.db-indicator--qa {
  color: #b45309;
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.4);
}

.db-indicator--prod {
  color: #b91c1c;
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.45);
  animation: db-indicator-pulse 1.8s ease-in-out infinite;
}

.db-indicator--prod .db-indicator__dot {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
}

@keyframes db-indicator-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
  50%      { box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18); }
}

/* Check-in time, shown just after the greeting name. */
/* Check-in status, sits on the left of the subheader (replaces breadcrumb). */
.sub-checkin {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  padding: 0.15rem 0.4rem;
  margin-left: -0.4rem;
  border-radius: 6px;
  color: #0e5b46;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-decoration: none;
  cursor: pointer;
  transition: color 0.12s ease, background 0.12s ease;
}

.sub-checkin:hover {
  text-decoration: none;
  color: #16a07a;
  background: rgba(22, 160, 122, 0.08);
}

.sub-checkin--out:hover {
  color: #475569;
  background: #f1f5f9;
}

/* Mapped but no punch today — red, since the staffer needs to act on it. */
.sub-checkin--alert {
  color: #b91c1c;
}

.sub-checkin--alert .sub-checkin__icon {
  color: #ef4444;
}

.sub-checkin--alert:hover {
  color: #991b1b;
  background: rgba(239, 68, 68, 0.08);
}

.sub-checkin:focus { outline: 0; }
.sub-checkin:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.sub-checkin__icon {
  flex: 0 0 auto;
  color: #16a07a;
}

.sub-checkin__text {
  min-width: 0;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sub-checkin__time {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Not checked in yet — muted so it reads as a gentle reminder, not an alert. */
.sub-checkin--out {
  color: #64748b;
}

.sub-checkin--out .sub-checkin__icon {
  color: #94a3b8;
}

.quick-replies-toggle {
  cursor: pointer;
  border-radius: 8px;
  transition: background 0.12s ease, color 0.12s ease;
}

.quick-replies-toggle:hover {
  background: rgba(22, 160, 122, 0.1);
  color: #16a07a;
}

.quick-replies-toggle--active {
  background: rgba(22, 160, 122, 0.14);
  color: #16a07a;
}

/* ── Calendar dropdown ───────────────────────────────────────────── */
.cal-dropdown__toggle {
  position: relative;
}

.cal-dropdown__badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
  letter-spacing: 0.02em;
  box-shadow: 0 0 0 2px #ffffff;
}

.cal-dropdown ::v-deep .cal-dropdown__menu {
  width: 300px;
  padding: 0;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.cal-dropdown__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  padding: 0.6rem 0.7rem;
  background: #fbfcfe;
  border-bottom: 1px solid #e5e9f2;
}

.cal-dropdown__header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
}

.cal-dropdown__title-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 auto;
  min-width: 0;
}

.cal-dropdown__title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1f2a44;
}

.cal-dropdown__today-link {
  margin-top: 1px;
  background: transparent;
  border: 0;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #16a07a;
  cursor: pointer;
  padding: 0;
}

.cal-dropdown__today-link:hover { color: #0e5b46; }
.cal-dropdown__today-link:focus,
.cal-dropdown__today-link:focus-visible { outline: 0; box-shadow: none; }

.cal-dropdown__nav-btn {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid #e5e9f2;
  border-radius: 6px;
  color: #4b5563;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.cal-dropdown__nav-btn:hover {
  background: #f5fbf8;
  border-color: #16a07a;
  color: #0e5b46;
}

.cal-dropdown__nav-btn:focus,
.cal-dropdown__nav-btn:focus-visible { outline: 0; box-shadow: none; }

.cal-dropdown__weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 0.5rem 0.55rem 0.25rem;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #94a3b8;
  text-align: center;
  text-transform: uppercase;
}

.cal-dropdown__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  padding: 0 0.55rem 0.5rem;
}

.cal-dropdown__cell {
  position: relative;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 0;
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 500;
  color: #1f2a44;
  cursor: pointer;
  transition: background 0.1s ease, color 0.1s ease;
}

.cal-dropdown__cell:hover {
  background: #f5fbf8;
  color: #0e5b46;
}

.cal-dropdown__cell:focus,
.cal-dropdown__cell:focus-visible { outline: 0; box-shadow: none; }

.cal-dropdown__cell--outside { color: #cbd5e1; }
.cal-dropdown__cell--weekend:not(.cal-dropdown__cell--outside) { color: #475569; }

.cal-dropdown__cell--today {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff !important;
  font-weight: 700;
}

.cal-dropdown__cell--today:hover { color: #ffffff; }

.cal-dropdown__cell-dot {
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #ef4444;
}

.cal-dropdown__cell--today .cal-dropdown__cell-dot {
  background: #ffffff;
}

.cal-dropdown__events {
  border-top: 1px solid #eef1f7;
  padding: 0.55rem 0.7rem 0.5rem;
  background: #fbfcfe;
}

.cal-dropdown__events-head {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 0.35rem;
}

.cal-dropdown__events-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.cal-dropdown__events-item {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.74rem;
  color: #1f2a44;
}

.cal-dropdown__events-dot {
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.cal-dropdown__events-dot--national   { background: #ef4444; }
.cal-dropdown__events-dot--religious  { background: #8b5cf6; }
.cal-dropdown__events-dot--cultural   { background: #f59e0b; }
.cal-dropdown__events-dot--observance { background: #0ea5e9; }

.cal-dropdown__events-date {
  flex: 0 0 auto;
  font-weight: 600;
  color: #4b5563;
  font-variant-numeric: tabular-nums;
}

.cal-dropdown__events-name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #1f2a44;
}

.cal-dropdown__footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  padding: 0.6rem 1rem;
  background: #fbfcfe;
  border-top: 1px solid #e5e9f2;
  cursor: pointer;
  font-size: 0.74rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #16a07a;
  text-decoration: none;
  transition: background 0.12s ease, color 0.12s ease;
}

.cal-dropdown__footer:hover {
  background: #f1f4fa;
  color: #0e5b46;
  text-decoration: none;
}

/* ── Mobile organisation switcher (replaces centered logo on < lg) ─── */
.mobile-org-switcher {
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 60vw;
}

.mobile-org-switcher__trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  max-width: 100%;
  padding: 0.3rem 0.55rem 0.3rem 0.35rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 999px;
  color: #1f2a44;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  line-height: 1.15;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.mobile-org-switcher__trigger:hover:not(:disabled),
.mobile-org-switcher__trigger--open {
  background: #f5fbf8;
  border-color: rgba(22, 160, 122, 0.4);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.mobile-org-switcher__trigger:focus { outline: 0; }
.mobile-org-switcher__trigger::-moz-focus-inner { border: 0; }
.mobile-org-switcher__trigger:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.mobile-org-switcher__trigger:disabled {
  cursor: default;
}

.mobile-org-switcher__trigger--static,
.mobile-org-switcher__trigger--static:disabled {
  cursor: default;
  opacity: 1;
}

.mobile-org-switcher__trigger--static:hover {
  background: #ffffff;
  border-color: #e5e9f2;
  box-shadow: none;
}

.mobile-org-switcher__avatar {
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #ffffff;
  box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
}

.mobile-org-switcher__avatar img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.mobile-org-switcher__avatar--sm {
  width: 26px;
  height: 26px;
  border-radius: 6px;
}

.mobile-org-switcher__initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  font-size: 0.72rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
}

.mobile-org-switcher__avatar--sm .mobile-org-switcher__initials {
  font-size: 0.62rem;
}

.mobile-org-switcher__info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.mobile-org-switcher__label {
  font-size: 0.55rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #94a3b8;
  line-height: 1;
}

.mobile-org-switcher__name {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1f2a44;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-org-switcher__chevron {
  flex: 0 0 auto;
  color: #94a3b8;
  transition: transform 0.18s ease, color 0.15s ease;
}

.mobile-org-switcher__trigger:hover .mobile-org-switcher__chevron,
.mobile-org-switcher__trigger--open .mobile-org-switcher__chevron {
  color: #0e5b46;
}

.mobile-org-switcher__trigger--open .mobile-org-switcher__chevron {
  transform: rotate(180deg);
}

.mobile-org-switcher__spinner {
  flex: 0 0 auto;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(22, 160, 122, 0.18);
  border-top-color: #16a07a;
  animation: mobile-org-switcher-spin 0.65s linear infinite;
}

@keyframes mobile-org-switcher-spin {
  to { transform: rotate(360deg); }
}

.mobile-org-switcher__menu {
  position: absolute;
  top: calc(100% + 0.4rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: 1050;
  width: min(280px, calc(100vw - 1.5rem));
  margin: 0;
  padding: 0;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.22);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: min(60vh, 360px);
}

.mobile-org-switcher__menu::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  z-index: 1;
}

.mobile-org-switcher__menu-head {
  padding: 0.7rem 0.8rem 0.45rem;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #6b7280;
  background: #fbfcfe;
  border-bottom: 1px solid #eef1f7;
}

.mobile-org-switcher__menu-list {
  margin: 0;
  padding: 0.3rem;
  list-style: none;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.mobile-org-switcher__menu-list::-webkit-scrollbar { width: 5px; }
.mobile-org-switcher__menu-list::-webkit-scrollbar-track { background: transparent; }
.mobile-org-switcher__menu-list::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 999px;
}

.mobile-org-switcher__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.55rem;
  background: transparent;
  border: 0;
  border-radius: 7px;
  color: #1f2a44;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s ease, color 0.12s ease;
}

.mobile-org-switcher__item:hover,
.mobile-org-switcher__item:focus-visible {
  background: #f5fbf8;
  color: #0e5b46;
  outline: 0;
}

.mobile-org-switcher__item--active {
  background: #dff5ea;
  color: #0e5b46;
}

.mobile-org-switcher__item--active:hover {
  background: #cdeede;
}

.mobile-org-switcher__item-name {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.84rem;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-org-switcher__item--active .mobile-org-switcher__item-name {
  font-weight: 700;
}

.mobile-org-switcher__check {
  flex: 0 0 auto;
  color: #16a07a;
}

.mobile-org-switcher-fade-enter-active,
.mobile-org-switcher-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.mobile-org-switcher-fade-enter,
.mobile-org-switcher-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -4px);
}

/* ── Mobile chrome tweaks ────────────────────────────────────────── */
@media (max-width: 991.98px) {
  /* CHeader uses 56px padding on the right by default; tighten so the
     account avatar + bell don't crowd the screen edge. */
  .header-nav {
    align-items: center;
  }

  /* Cap the notification menu so it doesn't punch through the viewport
     edge on phones. CoreUI positions it bottom-end relative to the bell,
     so it can otherwise be wider than the visible width. */
  .messages-dropdown ::v-deep .messages-dropdown__menu {
    width: min(340px, calc(100vw - 1rem));
    max-width: calc(100vw - 1rem);
  }

  /* The subheader row carries the breadcrumb + network pill. On phones
     the breadcrumb can be longer than the row; let it ellipsize instead
     of wrapping over the pill. */
  .subheader-row {
    padding-left: 0.6rem;
    padding-right: 0.6rem;
    gap: 0.35rem;
  }

  .subheader-row ::v-deep .breadcrumb {
    min-width: 0;
    flex-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.78rem;
  }

  .subheader-row ::v-deep .breadcrumb-item {
    white-space: nowrap;
  }

  .net-status__label {
    /* The pill already shows a coloured dot; on phones the word adds
       noise, so collapse to dot-only and keep the link tappable. */
    display: none;
  }

  .net-status__link {
    padding: 0.25rem;
  }

  .net-status__dot {
    width: 9px;
    height: 9px;
  }
}

@media (max-width: 575.98px) {
  .mobile-org-switcher {
    max-width: 52vw;
  }

  .mobile-org-switcher__name {
    font-size: 0.74rem;
  }

  .mobile-org-switcher__trigger {
    padding: 0.25rem 0.5rem 0.25rem 0.3rem;
    gap: 0.45rem;
  }

  .mobile-org-switcher__avatar {
    width: 28px;
    height: 28px;
    border-radius: 7px;
  }
}
</style>